import { useEffect } from 'react'

import { useCustomerAgentQueries } from 'hooks-queries/customer-agent'
import { customerAgent } from 'lib/customer-agent'

import { GET_HUBSPOT_VISITOR_IDENTIFICATION_LOG } from './use-customer-agent-identity.consts'
import type { IUseCustomerAgentIdentityParams } from './use-customer-agent-identity.types'

/**
 * Fetches the token that proves who the customer is to the HubSpot Customer Agent.
 *
 * Gated on the *resolved* user, not merely an authenticated one: `getCurrentWorkspace` only
 * validates the workspace header against the user's own workspaces once the user is in the store.
 *
 * Failure is silent — nothing is injected, support opens anonymous, the app stays up.
 */
export const useCustomerAgentIdentity = ({ email }: IUseCustomerAgentIdentityParams): void => {
  const { data, refetch } = useCustomerAgentQueries({
    queryName: 'getHubspotVisitorIdentification',
    errorLog: GET_HUBSPOT_VISITOR_IDENTIFICATION_LOG,
    options: { skip: !email },
  })

  const identity = data?.hubspotVisitorIdentification

  // An external-system write, which is what an effect is for. No cleanup: the identity must
  // outlive this render — dropping it is the logout's job, via `endSession`.
  useEffect(
    function publishIdentityToWidget() {
      if (!identity) return

      customerAgent.setIdentity({
        email: identity.email,
        token: identity.token,
        expiresAt: identity.expiresAt,
        refresh: () => {
          refetch()
        },
      })
    },
    [identity, refetch]
  )
}
